import { useMutation, useQueryClient } from "@tanstack/vue-query";
import Dialer from "@/services/dialer/dialer";
import type {
  ActiveDialerSession,
  DialerSession,
  UpdateDialerSessionAIAssistant,
} from "@/dtos/Dialer.dto";

export function usePauseDialerSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<DialerSession> => {
      const result = await Dialer.pauseSession();
      if (!result.data.status) {
        throw new Error(result.data.msg || "Failed to pause dialer session");
      }
      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dialerSession", "active"] });
    },
  });
}

export function useResumeDialerSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<DialerSession> => {
      const result = await Dialer.resumeSession();
      if (!result.data.status) {
        throw new Error(result.data.msg || "Failed to resume dialer session");
      }
      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dialerSession", "active"] });
    },
  });
}

// Applies from the next call on � the dialer reads the setting off the active
// session each time a call connects.
export function useUpdateDialerSessionAIAssistant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateDialerSessionAIAssistant): Promise<DialerSession> => {
      const result = await Dialer.updateSessionAIAssistant(data);
      if (!result.data.status) {
        throw new Error(result.data.msg || "Failed to update AI call assistant");
      }
      return result.data.data;
    },
    onSuccess: (session) => {
      queryClient.setQueryData<ActiveDialerSession | null>(["dialerSession", "active"], (prev) =>
        prev ? { ...prev, session: { ...prev.session, ...session } } : prev
      );
    },
  });
}

export function useEndDialerSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<DialerSession> => {
      const result = await Dialer.endSession();
      if (!result.data.status) {
        throw new Error(result.data.msg || "Failed to end dialer session");
      }
      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dialerSession", "active"] });
    },
  });
}

// Soft delete — the backend only allows it for ended sessions. The session
// drops out of history, but its calls stay in regular call history.
export function useDeleteDialerSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<{ id: string }> => {
      const result = await Dialer.deleteSession(id);
      if (!result.data.status) {
        throw new Error(result.data.msg || "Failed to delete dialer session");
      }
      return result.data.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["dialerSession", "list"] });
      queryClient.removeQueries({ queryKey: ["dialerSession", "detail", id] });
    },
  });
}
